import _, { isNaN } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import NgpOneButtonDialog from '../../dialog-one-button/NgpOneButtonDialog';
import { getSystemTime } from '../../../stores/SystemTimeStore';
import { CALLEE, CALLER } from './VoipTestConstants';

const M_LABEL = ' m';
const S_LABEL = ' s';
const MS_LABEL = ' ms';

export default function VoipTestNowResultsCallFlow({ callFlows, classes }) {
    const theme = useTheme();
    const isDarkMode = theme.palette.type === 'dark';
    const [someSIPMsgs, setSomeSIPMsgs] = useState([]);
    const [selectedSipMsg, setSelectedSipMsg] = useState(null);
    const sipMsgCountRef = useRef(0);
    const sipMsgsRef = useRef({});
    const filterSIPMsgTypesAryRef = useRef([]);
    const lastTimeDeltaRef = useRef(0);

    useEffect(() => {
        const bodyClass = 'sip-message-dialog-open';

        if (selectedSipMsg !== null) {
            document.body.classList.add(bodyClass);
        } else {
            document.body.classList.remove(bodyClass);
        }

        return () => {
            document.body.classList.remove(bodyClass);
        };
    }, [selectedSipMsg]);

    /**
     * Given a flow, a base date/time, and if this is the caller, this method builds a new multi-dimensional array of
     *  the SIP message flow data.  We add into this array (a) the time delta for each SIP message from the start,
     *  (b) if it is a message for the caller, and (c) the original SIP message data.  Given this form, we can join the
     *  two call flows into one and sort it to get everything in order.

     * @param flow
     * @param baseDtTm
     * @param isCaller
     * @returns {Array}
     */
    const calcTimeDeltas = useCallback((flow, baseDtTm, isCaller) => {
        const newFlow = [];

        _.each(flow, (msg) => {
            msg = _.isObject(msg) ? msg : JSON.parse(msg);

            // Add an ID so we can get back to this SIP message easily later.
            msg.id = sipMsgCountRef.current;
            sipMsgCountRef.current += 1;

            const timeDelta = Date.parse(msParseFix(msg.time)) - baseDtTm;
            newFlow.push([timeDelta, { msg, isCaller }]);
        });

        return newFlow;
    }, []);

    /**
     * Combines the two call flows into one array of SIP messages.  Each elements in the array is as follows:
     *
     * [ timeDelta from start, {isCaller: true|false, msg: SIP Message Object} ]
     *
     * @param callerFlow
     * @param calleeFlow
     * @returns Array of arrays, sorted by date/time
     */
    const mergeCallFlows = useCallback(
        (callerFlow, calleeFlow) => {
            const now = getSystemTime();
            let callerFirstMsg = null;
            let callerFirstDtTm = now;
            let calleeFirstMsg = null;
            let calleeFirstDtTm = now;

            // Determine the FIRST date/time value between the caller and callee
            if (callerFlow.length > 0) {
                callerFirstMsg = _.isObject(callerFlow[0]) ? callerFlow[0] : JSON.parse(callerFlow[0]);
                callerFirstDtTm = Date.parse(msParseFix(callerFirstMsg.time));
            }

            if (calleeFlow.length > 0) {
                calleeFirstMsg = _.isObject(calleeFlow[0]) ? calleeFlow[0] : JSON.parse(calleeFlow[0]);
                calleeFirstDtTm = Date.parse(msParseFix(calleeFirstMsg.time));
            }

            const firstTimesDiff = callerFirstDtTm - calleeFirstDtTm;
            const baseDtTm = firstTimesDiff < 0 ? callerFirstDtTm : calleeFirstDtTm;

            // Transform the call flow arrays into 2-D arrays with the time delta in element sub zero, and
            //  sub 1 as a Hash of isCaller and the message data
            const callerMsgs = calcTimeDeltas(callerFlow, baseDtTm, true);
            const calleeMsgs = calcTimeDeltas(calleeFlow, baseDtTm, false);

            // Combine the to arrays into one array, and sort by time delta
            const allMsgs = callerMsgs.concat(calleeMsgs);
            allMsgs.sort((a, b) => {
                // If the two times are the same, fall back on their order in the original arrays (the ID value tells us that)
                const deltaA = parseInt(a[0], 10);
                const deltaB = parseInt(b[0], 10);
                let out = parseInt(a[0], 10) - parseInt(b[0], 10);

                if (deltaA === deltaB) {
                    out = a[1].msg.id - b[1].msg.id;
                }

                return out;
            });

            // Return it
            return allMsgs;
        },
        [calcTimeDeltas]
    );

    const buildCallFlow = useCallback(() => {
        const callerFlow = callFlows[CALLER] || [];
        const calleeFlow = callFlows[CALLEE] || [];

        // Combine the two call flows, with messages in order by delta from the first date/time
        const sipFlow = mergeCallFlows(callerFlow, calleeFlow);

        // Render it
        setSomeSIPMsgs(sipFlow);
    }, [callFlows, mergeCallFlows]);

    useEffect(() => {
        (async () => {
            buildCallFlow();
        })();
    }, [buildCallFlow]);

    /**
     * Used in the building of the SIP Call Flow log.  This method formats the string shown the furthest left column
     *  for display.
     *
     * @param timeDelta  A number of milliseconds between when a SIP message was sent, and the start
     * @returns {string} e.g., + 54ms, or + 9.529s
     */
    function formatTimeDelta(timeDelta) {
        let out = `+ ${timeDelta}${MS_LABEL}`;
        let m;
        let s;

        if (timeDelta === 0) {
            out = '';
        } else if (timeDelta > 60000) {
            m = parseInt(timeDelta / 60000, 10);
            s = parseFloat((timeDelta % 60000) / 1000);
            s = Math.round(s * 1000) / 1000;
            out = `+ ${m}${M_LABEL} ${s}${S_LABEL}`;
        } else if (timeDelta > 1000) {
            s = parseFloat(timeDelta / 1000);
            s = Math.round(s * 1000) / 1000;
            out = `+ ${s}${S_LABEL}`;
        }

        return out;
    }

    /**
     * Builds the hyperlink and message text for the message type
     *
     * @param sipInfo     Details of SIP message given to us by the probes
     * @returns {string}
     */
    function appendMessageType(sipInfo) {
        if (sipInfo.raw_msg !== undefined) {
            const id = _.uniqueId('sipInfo');
            const newSipMsgs = { ...sipMsgsRef.current };
            newSipMsgs[id] = sipInfo.raw_msg;
            sipMsgsRef.current = newSipMsgs;

            return (
                <>
                    <button
                        id={id}
                        type="button"
                        title="Click for SIP message text"
                        className="sip-link"
                        onClick={() => setSelectedSipMsg(sipInfo.raw_msg)}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            color: theme.palette.type === 'dark' ? '#4597CB' : '#3A6178',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            font: 'inherit',
                        }}
                    >
                        {sipInfo.type}
                    </button>
                    {'\u00A0\u00A0'}
                </>
            );
        }

        return `${sipInfo.type}  `;
    }

    /**
     * Adds the response code and text if certain conditions are met
     *
     * @param direction - RX or TX - Receive or SEnd
     * @param code      - Response Code
     * @param text      - Response Text
     * @returns {string}
     */
    function appendResponseInfo(direction, code, text) {
        let out = '';

        if (isNaN(code)) {
            code = 0;
        }

        // MDL - After a conversation with Warren and Antoine, we decided to show the code and text
        //  regardless of the message direction
        // if ((direction === 'RX') && (parseInt(code) > 99)) {
        if (parseInt(code, 10) > 99) {
            out = `${code} ${text}`;
        }

        return out;
    }

    function getArrow(direction, isCaller) {
        const transmit = 'TX';

        if (isCaller) {
            if (direction === transmit) {
                return (
                    <>
                        <td className="td-right">
                            <i className="fa fa-arrow-right" />
                        </td>
                        <td className={`call-flow-cm-cell ${classes.callFlowCmCell}`}>&nbsp;</td>
                    </>
                );
            }
            return (
                <>
                    <td>&nbsp;</td>
                    <td className={`call-flow-cm-cell ${classes.callFlowCmCell}`}>
                        <i className="icon-arrow-left" />
                    </td>
                </>
            );
        }
        if (direction === transmit) {
            return (
                <>
                    <td className={`call-flow-cm-cell ${classes.callFlowCmCell}`}>&nbsp;</td>
                    <td className="td-left">
                        <i className="fa fa-arrow-left" />
                    </td>
                </>
            );
        }
        return (
            <>
                <td className={`call-flow-cm-cell ${classes.callFlowCmCell} td-right`}>
                    <i className="fa fa-arrow-right" />
                </td>
                <td>&nbsp;</td>
            </>
        );
    }

    /**
     * Little method to filter out any messages we don't want to show on the Call Flow
     * @param sipMsg
     * @returns {boolean}
     */
    function filterSIPMessageType(sipMsg) {
        let filterMsg = false;

        if (sipMsg) {
            if (sipMsg.type !== undefined) {
                if (_.indexOf(filterSIPMsgTypesAryRef.current, sipMsg.type.toLowerCase()) > -1) {
                    filterMsg = true;
                }
            }
        }

        return filterMsg;
    }

    /**
     * Dates in SIP messages, stored in test_time field, will have miliseconds like '.70Z' for 70ms.  This is bad since
     * JavaScript sees '70Z' as == '700Z'.  Basically, it's 7 hundreds and 0 Tens.  To get around this, we have to isolate
     * the milliseconds and pre-pend a zero if it's too short. Same logic applies to single digit millisecond value such
     * as '.7Z' for 7ms. Zero's have to be pre-pended to JavaScript to convert properly.
     *
     * @param dateTime   String value like '2014-04-07T19:27:27.70Z'
     * @returns {string}
     */
    function msParseFix(dateTime) {
        let parts;
        let returning = '';

        if (dateTime) {
            parts = dateTime.split('.');
            if (parts[1].length === 2) {
                parts[1] = `00${parts[1]}`;
            } else if (parts[1].length === 3) {
                parts[1] = `0${parts[1]}`;
            }

            returning = parts.join('.');
        }

        return returning;
    }

    function CallFlowTable() {
        const rows = [];
        let lastTimeDelta = 0;

        _.each(someSIPMsgs, (msg, idx) => {
            const timeDelta = msg[0];
            const { isCaller } = msg[1];
            const sipInfo = msg[1].msg;

            const deltaDelta = timeDelta - lastTimeDelta;

            if (!filterSIPMessageType(sipInfo)) {
                if (deltaDelta > 3000) {
                    rows.push(
                        <tr key={`gap-${idx}`}>
                            <td colSpan="6" className={classes.callFlowBreak}>
                                {formatTimeDelta(deltaDelta)}
                            </td>
                        </tr>
                    );
                }

                if (isCaller) {
                    rows.push(
                        <tr key={`row-${idx}`}>
                            <td className={classes.tdRight}>{formatTimeDelta(timeDelta)}</td>
                            <td className={classes.tdCenter}>
                                {appendMessageType(sipInfo)}
                                {appendResponseInfo(sipInfo.direction, sipInfo.response_code, sipInfo.response_text)}
                            </td>
                            {getArrow(sipInfo.direction, isCaller)}
                            <td />
                            <td />
                        </tr>
                    );
                } else {
                    rows.push(
                        <tr key={`row-${idx}`}>
                            <td className={classes.tdRight}>{formatTimeDelta(timeDelta)}</td>
                            <td />
                            <td />
                            {getArrow(sipInfo.direction, isCaller)}
                            <td className={classes.tdCenter}>
                                {appendMessageType(sipInfo)}
                                {appendResponseInfo(sipInfo.direction, sipInfo.response_code, sipInfo.response_text)}
                            </td>
                        </tr>
                    );
                }
            }

            lastTimeDelta = timeDelta;
        });

        lastTimeDeltaRef.current = lastTimeDelta;

        return (
            <table className={classes.tableStriped}>
                <tbody>
                    <tr height="1px">
                        <th className="col-width-90" />
                        <th className="col-width-275 td-center" />
                        <th className="col-width-50" />
                        <th className="col-width-30 td-center" />
                        <th className="col-width-50" />
                        <th className="td-center" />
                    </tr>
                    <tr>
                        <th>&nbsp;</th>
                        <th className="td-center">CALLER</th>
                        <th className="td-center" colSpan="3">
                            CALL MANAGER
                        </th>
                        <th className="td-center">CALLEE</th>
                    </tr>
                    {rows}
                </tbody>
            </table>
        );
    }

    return (
        <div className={`ngl-bootstrap-table-container ${classes.callFlowContainer}`}>
            <style>
                {`
                    body.sip-message-dialog-open .MuiDialog-container .MuiDialog-paper {
                        min-width: 240px;
                        max-width: 80%;
                        max-height: 80%;
                        position: relative;
                        overflow: auto;
                    }
                `}
            </style>
            <CallFlowTable classes={classes} />
            <NgpOneButtonDialog
                open={selectedSipMsg !== null}
                setOpen={(open) => {
                    if (!open) {
                        setSelectedSipMsg(null);
                    }
                }}
            >
                <div
                    style={{
                        backgroundColor: isDarkMode ? '#FFF' : theme.palette.background.paper,
                        color: isDarkMode ? '#111820' : theme.palette.text.primary,
                    }}
                >
                    <pre
                        id="html-response-modal"
                        style={{
                            backgroundColor: isDarkMode ? '#171717' : '#e0e0e0',
                            color: isDarkMode ? '#EAEAEA' : '#111820',
                            padding: '12px',
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                        }}
                    >
                        {selectedSipMsg}
                    </pre>
                </div>
            </NgpOneButtonDialog>
        </div>
    );
}
